<template>
  <div class="cameras-table">
    <button-group />
    <div class="field mb-4">
      <label class="checkbox">
        <input type="checkbox" v-model="suspendedOnly" @change="filterSuspended" />
        Suspended cameras only
      </label>
    </div>

    <div class="table-container">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Project</th>
            <th>Company</th>
            <th>Is Deleted</th>
          </tr>
        </thead>
        <!-- Skeleton Loading State -->
        <transition name="fade" mode="out-in">
          <tbody v-if="isLoadingSync" key="loading" class="skeleton-tbody">
            <tr
              v-for="n in skeletonRows"
              :key="`skeleton-${n}`"
              class="skeleton-row"
            >
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
            </tr>
          </tbody>

          <!-- Actual Data -->
          <tbody v-else-if="cameras.length > 0" key="data" class="data-tbody">
            <tr v-for="(cam, i) in cameras" :key="i">
              <td>{{ cam.id }}</td>
              <td :title="cam.name">
                <a href="#" @click.prevent="editCamera(cam)" class="entity-link"
                  >{{ truncate(cam.name, 30)
                  }}<span class="icon is-small edit-icon"
                    ><i class="fas fa-edit"></i></span
                ></a>
                <span v-if="cam.archived_at" class="tag ml-2 is-light" :title="`Archived ${suspensionDate(cam.archived_at)} UTC`">
                  Archived
                </span>
                <span v-if="cam.version >= 2 && cam.version <= 4 && cam.snapshot_suspended_at" class="tag ml-2 is-warning">
                  Snapshots paused
                </span>
                <span v-if="cam.version >= 2 && cam.version <= 4 && cam.snapshot_suspended_at" class="ml-2">{{ suspensionDate(cam.snapshot_suspended_at) }} UTC</span>
              </td>
              <td :title="cam.project.name">
                <a
                  href="#"
                  @click.prevent="editProject(cam.project)"
                  class="entity-link"
                  >{{ truncate(cam.project.name, 30)
                  }}<span class="icon is-small edit-icon"
                    ><i class="fas fa-edit"></i></span
                ></a>
              </td>
              <td :title="cam.project.company.name">
                <a
                  href="#"
                  @click.prevent="editCompany(cam.project.company)"
                  class="entity-link"
                  >{{ truncate(cam.project.company.name, 30)
                  }}<span class="icon is-small edit-icon"
                    ><i class="fas fa-edit"></i></span
                ></a>
              </td>
              <td>
                <span
                  :class="['tag', cam.is_deleted ? 'is-danger' : 'is-success']"
                  >{{ cam.is_deleted ? "Deleted" : "Active" }}</span
                >
              </td>
            </tr>
          </tbody>

          <!-- Empty State -->
          <tbody v-else key="empty">
            <tr>
              <td colspan="5" class="has-text-centered empty-state">
                <div class="empty-state-content">
                  <p class="has-text-grey">
                    <span class="icon is-large">
                      <i class="fas fa-video fa-2x"></i>
                    </span>
                  </p>
                  <p class="has-text-grey">No cameras found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </transition>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { formatDate } from "@/utils/date_converter";
import ButtonGroup from "@/components/ButtonGroup.vue";
export default {
  components: {
    "button-group": ButtonGroup,
  },
  data() {
    return {
      skeletonRows: 10, // Number of skeleton rows to show during loading
    };
  },
  computed: {
    ...mapState(["cameras", "isLoadingSync"]),
    suspendedOnly: {
      get() { return this.$store.state.filters.snapshotSuspended; },
      set(value) { this.$store.commit("SET_FILTERS_SNAPSHOT_SUSPENDED", value); },
    },
  },

  methods: {
    ...mapActions([
      "fetchList",
      "openCameraModal",
      "openProjectModal",
      "openCompanyModal",
    ]),
    filterSuspended() {
      this.$store.commit("SET_CURRENT_PAGE", { tabIndex: this.$store.getters.activeTabIndex, page: 1 });
      this.fetchList();
    },
    suspensionDate(timestamp) {
      return formatDate(timestamp, { showRelative: false, timeZone: "UTC" });
    },
    truncate(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    editCamera(camera) {
      this.openCameraModal(camera);
    },
    editProject(project) {
      this.openProjectModal(project);
    },
    editCompany(company) {
      this.openCompanyModal(company);
    },
  },
  mounted() {
    this.fetchList();
  },
};
</script>

<style scoped>
.table-container {
  position: relative;
  min-height: 400px;
  transition: min-height 0.3s ease;
}

/* Skeleton Loading Styles */
.skeleton-row {
  pointer-events: none;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  display: inline-block;
}

.skeleton-text {
  height: 20px;
  width: 80%;
  max-width: 120px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Vue Transition Classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Empty state styling */
.empty-state {
  padding: 3rem !important;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.empty-state-content .icon {
  color: #dbdbdb;
}
</style>
